import React from 'react';

// Tier vocabulary is fixed in migration 044 / TierService: T1 Essential,
// T2 Standard, T3 Advanced. Rank compares tiers; a higher tier includes the
// lower ones (PR-211 section 4).
export type TierCode = 'T1' | 'T2' | 'T3';

export const TIERS: Record<TierCode, { name: string; rank: number; monthlyRateCents: number }> = {
  T1: { name: 'Essential', rank: 1, monthlyRateCents: 200 },
  T2: { name: 'Standard', rank: 2, monthlyRateCents: 350 },
  T3: { name: 'Advanced', rank: 3, monthlyRateCents: 500 },
};

export function isTierCode(v: unknown): v is TierCode {
  return v === 'T1' || v === 'T2' || v === 'T3';
}

export function tierName(code: string | null | undefined, fallback?: string): string {
  return isTierCode(code) ? TIERS[code].name : (fallback ?? String(code ?? ''));
}

/** True when `current` includes everything `required` includes. */
export function isEntitled(current: TierCode | null | undefined, required: TierCode): boolean {
  if (!isTierCode(current)) return false;
  return TIERS[current].rank >= TIERS[required].rank;
}

const badgeClass: Record<TierCode, string> = {
  T1: 'bg-white/[0.06] text-white/80 border-white/15',
  T2: 'bg-anchor-teal/10 text-anchor-teal border-anchor-teal/30',
  T3: 'bg-anchor-violet/10 text-anchor-violet border-anchor-violet/30',
};

interface TierBadgeProps {
  code: TierCode | string | null | undefined;
  /** Server-provided display name, when available; falls back to the fixed vocabulary. */
  name?: string;
  size?: 'sm' | 'md';
  /** Show the code before the name ("T2 Standard"). Default true. */
  showCode?: boolean;
  className?: string;
}

/**
 * The client's tier, as a pill. Colour carries no meaning on its own: the code
 * and name are always in the text.
 */
export const TierBadge: React.FC<TierBadgeProps> = ({ code, name, size = 'sm', showCode = true, className = '' }) => {
  if (!isTierCode(code)) {
    return (
      <span className={`inline-flex items-center rounded-full border border-white/10 text-white/50 ${size === 'md' ? 'px-3 py-1 text-xs' : 'px-2 py-0.5 text-[10px]'} ${className}`}>
        No tier
      </span>
    );
  }
  const label = name || TIERS[code].name;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border font-medium tracking-wide ${badgeClass[code]} ${size === 'md' ? 'px-3 py-1 text-xs' : 'px-2 py-0.5 text-[10px]'} ${className}`}
      title={`${code} ${label}`}
    >
      {showCode && <span className="font-mono opacity-80">{code}</span>}
      <span>{label}</span>
    </span>
  );
};

export default TierBadge;
